// Sons Neuro Quest synthétisés (Web Audio) — aucun fichier audio, désactivables.
import { useEffect, useState } from "react";

const KEY = "m2hat_nq_sound";
let ctx = null;
const listeners = new Set();

export const soundEnabled = () => { try { return localStorage.getItem(KEY) !== "off"; } catch { return true; } };
export const setSoundEnabled = (on) => { try { localStorage.setItem(KEY, on ? "on" : "off"); } catch { /* ignore */ } listeners.forEach((l) => l(on)); };
export const useSoundEnabled = () => {
  const [on, setOn] = useState(soundEnabled);
  useEffect(() => { listeners.add(setOn); return () => listeners.delete(setOn); }, []);
  return [on, setSoundEnabled];
};

const getCtx = () => {
  if (typeof window === "undefined") return null;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  if (!ctx) ctx = new AC();
  if (ctx.state === "suspended") ctx.resume().catch(() => {});
  return ctx;
};

// Joue une note : fréquence (Hz), durée (s), décalage (s), type d'onde, volume.
const tone = (freq, dur = 0.12, at = 0, type = "sine", vol = 0.18) => {
  const c = getCtx(); if (!c) return;
  const o = c.createOscillator(); const g = c.createGain();
  o.type = type; o.frequency.value = freq;
  const t = c.currentTime + at;
  g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(vol, t + 0.01); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  o.connect(g); g.connect(c.destination); o.start(t); o.stop(t + dur + 0.02);
};

const guard = (fn) => (...a) => { if (!soundEnabled()) return; try { fn(...a); } catch { /* ignore */ } };

export const sfx = {
  ok: guard(() => { tone(880, 0.09); tone(1320, 0.14, 0.08); }),
  ko: guard(() => { tone(220, 0.18, 0, "sawtooth", 0.12); tone(160, 0.22, 0.1, "sawtooth", 0.1); }),
  tick: guard(() => tone(660, 0.08, 0, "square", 0.08)),
  go: guard(() => { tone(880, 0.12, 0, "square", 0.12); tone(1320, 0.25, 0.1, "square", 0.12); }),
  fanfare: guard(() => { [523, 659, 784, 1047].forEach((f, i) => tone(f, 0.18, i * 0.13, "triangle", 0.16)); tone(1319, 0.5, 0.55, "triangle", 0.18); }),
  chest: guard(() => { [392, 523, 659, 784, 1047].forEach((f, i) => tone(f, 0.16, i * 0.09, "triangle", 0.14)); }),
};

// Emojis prononçables dans les consignes ; les autres sont retirés.
const EMOJI_WORDS = { "🟢": "la pastille verte", "🔴": "la pastille rouge", "🟡": "la pastille jaune", "🟣": "la pastille violette", "🛡️": "", "📡": "radar", "🐉": "le dragon vert", "🦊": "renards", "🟦": "carrés bleus", "⭐": "étoiles", "🌙": "la lune", "🪙": "pièces", "💎": "diamants", "🔔": "cloches", "⚙️": "le bouton stabiliser", "🚩": "le petit drapeau", "✅": "une coche verte", "📦": "la boîte", "🦎": "le caméléon", "👻": "le fantôme", "🗺️": "la carte", "🔦": "la lampe", "🍎": "la pomme", "📘": "le cahier bleu", "📗": "le cahier vert" };
export const speakable = (text) => Object.entries(EMOJI_WORDS).reduce((t, [e, w]) => t.split(e).join(` ${w} `), text)
  .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}\u{200D}]/gu, " ").replace(/\s+/g, " ").replace(/\s=\s/g, " veut dire ").replace(/\s([,.!?:])/g, "$1").trim();

// Lecture vocale (Web Speech API), français, débit lent pour les enfants.
export const speechSupported = () => typeof window !== "undefined" && "speechSynthesis" in window && "SpeechSynthesisUtterance" in window;
export const speak = (text, onEnd) => {
  if (!speechSupported()) return false;
  const synth = window.speechSynthesis;
  synth.cancel();
  const u = new SpeechSynthesisUtterance(speakable(text));
  u.lang = "fr-FR"; u.rate = 0.9; u.pitch = 1.05;
  try { const fr = synth.getVoices().find((v) => v.lang && v.lang.toLowerCase().startsWith("fr")); if (fr) u.voice = fr; } catch { /* voix par défaut */ }
  u.onend = () => onEnd && onEnd(); u.onerror = () => onEnd && onEnd();
  try { synth.speak(u); } catch { return false; }
  return true;
};
export const stopSpeaking = () => { if (speechSupported()) window.speechSynthesis.cancel(); };
